import React, { useCallback, useEffect, useState } from 'react';
import {
  downloadAnttInfraction,
  getInfraction,
  infraction,
  infractionPatchData,
  updateInfraction
} from 'services/antt';
import { Link, useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import CardContent from '@mui/material/CardContent';
import Tab from '@mui/material/Tab';
import TopPage from 'components/Shared/TopPage';
import Loader from 'components/Shared/Loader';
import { BILL_PAID, EMPTY_FIELD_VALUE } from 'helpers/constants';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import { CircularProgress } from '@mui/material';
import Badge from '@mui/material/Badge';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import TransportDocument from 'assets/images/transport-document.png';
import NoTransportDocument from 'assets/images/no-transport-document.png';
import { cnpjMask } from 'helpers/mask';
import { getOnlyDate, getOnlyTime } from 'helpers/date';
import { hasReportSource } from 'helpers/utils';
import { usePaymentStatusEmitter } from 'hooks';
import { getSapTransportData } from 'services/sap';
import { Attach, getAttachments } from 'services/antt/attachments';

import { InfractionResponse } from '../../../../helpers/types';
import Financial from './Financial';
import Attachments from './Attachments';
import Ascriptions from './Ascriptions';
import DraggableDialog from './DraggableDialog';
import Legal from './Legal';
import Logistics from './Logistics';
import FreightFloor from './FreightFloor';
import { TABS } from './constants';
import useStyles, {
  CardCustom,
  CardHeader,
  CustomTabs,
  ReportSourceAlert,
  SubmitButton,
  TopItem,
  TopTitle
} from './styles';

interface RenderFieldProps {
  label: string;
  value: string | null;
}

function RenderField({ label, value }: RenderFieldProps) {
  return (
    <div>
      <p>
        <span>{label}</span> {value ?? EMPTY_FIELD_VALUE}
      </p>
    </div>
  );
}

function TrafficTicketDetail() {
  const theme = useTheme();
  const classes = useStyles(theme)();
  const { trafficTicketId } = useParams();
  const LOGISTICS_TAB = 'Logística';
  const FREIGHT_FLOOR_TAB = 'Piso Mínimo de Frete';
  const LEGAL_TAB = 'Jurídico';
  const FINANCIAL_TAB = 'Financeiro';
  const ATTACHMENTS_TAB = 'Anexos';

  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [currTab, setCurrTab] = useState<number>(0);
  const [infraction, setInfraction] = useState<infraction>({} as infraction);
  const [infractionPathData, setInfractionPathData] = useState<infractionPatchData>({} as infractionPatchData);
  const [downloadingInfraction, setDownloadingInfraction] = useState<boolean>(false);

  const [sapTransportInfo, setSapTransportInfo] = useState<InfractionResponse | null>(null);
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const [attachments, setAttachments] = useState<number>(0);

  const newPaymentStatus = usePaymentStatusEmitter();

  const handleSuccess = (newData: infraction) => {
    setInfraction(newData);
    const details = JSON.parse(newData.details || '{}');

    setInfractionPathData({
      os_number: newData.os_number,
      os_date: newData.os_date,
      os_carrier_number: newData.os_carrier_number,
      os_additional_info: newData.os_additional_info,
      legal_assignment_date: newData.legal_assignment_date,
      legal_instructions: newData.legal_instructions,
      legal_is_with_legal: newData.legal_is_with_legal,
      legal_additional_info: newData.legal_additional_info,
      finance_assignment_date: newData.finance_assignment_date,
      finance_is_with_finance: newData.finance_is_with_finance,
      finance_payment_order: newData.finance_payment_order,
      finance_request_date: newData.finance_request_date,
      finance_sent_date: newData.finance_sent_date,
      finance_instructions: newData.finance_instructions,
      finance_additional_info: newData.finance_additional_info,
      carrier_name: details.transportadora,
      responsible: details.responsavel
    });

    setIsFetching(false);
  };

  const fetchData = useCallback(() => {
    setIsFetching(true);
    getInfraction(handleSuccess, Number(trafficTicketId));
  }, [trafficTicketId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const changedInfractionData = (data: Partial<infractionPatchData>) => {
    setInfractionPathData({
      ...infractionPathData,
      ...data
    });
  };

  const updateInfractionData = () => {
    setIsFetching(true);

    updateInfraction(
      Number(trafficTicketId),
      infractionPathData,
      () => fetchData(),
      (error: string) => {
        setIsFetching(false);
        console.error(error);
      }
    );
  };

  const handleChangTab = (event: React.SyntheticEvent, newValue: number) => {
    setCurrTab(newValue);
  };

  const { number, type, status, nickname, id, extrato_situacao, invoice_status, cnpj } = infraction;

  const isReportSource = hasReportSource(infraction.details);

  const hasFreightData = Boolean(infraction.distance || infraction.cc_coefficient || infraction.ccd_coefficient);
  const [firstTab, ...otherTabs] = TABS;
  const tabs = hasFreightData ? [firstTab, FREIGHT_FLOOR_TAB, ...otherTabs] : TABS;
  const currentTab = tabs[currTab];

  const handleGetAttachments = useCallback((attachmentsList: Attach[]) => {
    setAttachments(attachmentsList.length);
  }, []);

  useEffect(() => {
    if (!id) return;
    getAttachments(handleGetAttachments, id);
  }, [handleGetAttachments, id]);

  const handleDownloadAuto = () => {
    setDownloadingInfraction(true);

    downloadAnttInfraction(id, number, () => {
      setDownloadingInfraction(false);
    });
  };

  const getStatusPayment = () => {
    if (extrato_situacao === BILL_PAID) return 'paid';

    if (newPaymentStatus === 'pending' || invoice_status === 'Pago') return 'pending';

    return 'unpaid';
  };

  const statusPayment = getStatusPayment();

  const fetchSapTransportData = useCallback(() => {
    const handleSuccessSap = (response: InfractionResponse) => {
      setSapTransportInfo(response);
    };

    getSapTransportData(id, handleSuccessSap);
  }, [id]);

  useEffect(() => {
    if (!id) return;
    fetchSapTransportData();
  }, [fetchSapTransportData, id]);

  const openDraggableDialog = () => {
    setOpenDialog(true);
  };

  return (
    <>
      <TopPage title='Multa - Detalhes' />

      {isFetching ? (
        <Loader />
      ) : (
        <Grid container>
          <Grid item xs={9}>
            <CardHeader>
              <div className={classes.topInfo}>
                <TopTitle>
                  <div>
                    <h2>Auto de infração</h2>
                    <p>{number ?? EMPTY_FIELD_VALUE}</p>
                  </div>

                  <div>
                    <LoadingButton
                      variant='contained'
                      size='small'
                      disableElevation
                      onClick={handleDownloadAuto}
                      loading={downloadingInfraction}
                      loadingIndicator={<CircularProgress size={16} />}
                      startIcon={downloadingInfraction ? null : <DownloadRoundedIcon />}
                    >
                      {downloadingInfraction ? '' : 'Baixar auto de infração'}
                    </LoadingButton>
                  </div>
                </TopTitle>

                <TopItem>
                  <RenderField label='Status:' value={status} />
                  <RenderField label='Tipo da Multa:' value={type} />
                  <RenderField label='CNPJ:' value={cnpjMask(cnpj)} />
                  <RenderField label='Empresa:' value={nickname} />
                </TopItem>
              </div>
            </CardHeader>

            <CustomTabs value={currTab} onChange={handleChangTab} className='welcome-tutorial'>
              {tabs.map(tab => (
                <Tab
                  key={tab}
                  label={
                    tab === ATTACHMENTS_TAB && attachments > 0 ? (
                      <Badge
                        badgeContent={attachments}
                        className={currentTab === ATTACHMENTS_TAB ? classes.badgeAttachments : classes.badge}
                      >
                        {tab}
                      </Badge>
                    ) : (
                      tab
                    )
                  }
                />
              ))}
            </CustomTabs>

            <CardCustom className={classes.container}>
              <CardContent className={classes.cardContent}>
                <div>
                  <div className={classes.tabsContainer}>
                    {currentTab === LOGISTICS_TAB && (
                      <Logistics infractionData={infraction} onChange={changedInfractionData} />
                    )}

                    {currentTab === FREIGHT_FLOOR_TAB && <FreightFloor infractionData={infraction} />}

                    {currentTab === LEGAL_TAB && <Legal infractionData={infraction} onChange={changedInfractionData} />}

                    {currentTab === FINANCIAL_TAB && (
                      <Financial
                        infractionData={infraction}
                        onChange={changedInfractionData}
                        paymentStatus={statusPayment}
                      />
                    )}

                    {currentTab === ATTACHMENTS_TAB && <Attachments infractionData={infraction} />}
                  </div>

                  {currentTab !== ATTACHMENTS_TAB && currentTab !== FREIGHT_FLOOR_TAB && (
                    <div className={classes.bottomButtons}>
                      <Link to='/multas'>
                        <Button variant='text'>Voltar</Button>
                      </Link>

                      <SubmitButton
                        color='secondary'
                        variant='contained'
                        onClick={updateInfractionData}
                        disableElevation
                      >
                        Salvar
                      </SubmitButton>
                    </div>
                  )}
                </div>
              </CardContent>
            </CardCustom>
          </Grid>

          <Grid item xs={3}>
            {currentTab === LOGISTICS_TAB && (sapTransportInfo?.data?.length ?? 0) > 0 && (
              <>
                <CardCustom className={classes.transportDocument}>
                  <Badge badgeContent={sapTransportInfo?.data?.length} color='primary'>
                    <img src={TransportDocument} alt='Transport document' />
                  </Badge>

                  <p>Essa infração possui dados de transporte para consulta</p>

                  <Button size='small' variant='text' aria-label='add' onClick={openDraggableDialog}>
                    Consultar
                  </Button>
                </CardCustom>

                <DraggableDialog
                  open={openDialog}
                  data={sapTransportInfo?.data ?? []}
                  onClose={() => setOpenDialog(false)}
                />
              </>
            )}

            {currentTab === LOGISTICS_TAB &&
              sapTransportInfo?.integration_active &&
              !sapTransportInfo?.data?.length && (
                <CardCustom className={classes.transportEmpty}>
                  <Badge badgeContent={0} showZero color='primary'>
                    <img src={NoTransportDocument} alt='No transport document' />
                  </Badge>

                  <p>Nenhum dado de transporte foi encontrado para esta infração na integração SAP</p>

                  {sapTransportInfo?.last_sync_attempt && (
                    <span className={classes.lastSync}>
                      Última consulta em {getOnlyDate(sapTransportInfo.last_sync_attempt)} às{' '}
                      {getOnlyTime(sapTransportInfo.last_sync_attempt)}
                    </span>
                  )}
                </CardCustom>
              )}

            {isReportSource && (
              <ReportSourceAlert role='status' sx={{ ml: 2.5 }}>
                <WarningAmberRoundedIcon className='alert-icon' />

                <div>
                  <span className='alert-title'>Dados Incompletos</span>
                  <p>
                    Este auto de infração foi obtido a partir de <strong>relatórios da ANTT</strong> e, por isso,
                    algumas informações apresentadas estão <strong>incompletas</strong>. A indisponibilidade de
                    determinados dados ocorre por um <strong>problema sistêmico</strong> da{' '}
                    <strong>Área do Autuado SIFAMA — ANTT</strong>, cujo acesso aos{' '}
                    <strong>dados complementares, auto de infração e vistas do processo</strong> estão indisponíveis.
                  </p>
                  <p>
                    <strong>IMPORTANTE</strong>: Pensando no melhor para você, o time UP Fleet conseguiu uma agenda com
                    a <strong>Superintendência da ANTT em 13/07/2026</strong>, reportando o problema e evidenciando os
                    impactos para todo o segmento de transportes.
                  </p>
                </div>
              </ReportSourceAlert>
            )}

            <CardCustom className={classes.ascriptions}>
              <CardContent>{isFetching ? <Loader /> : <Ascriptions infractionData={infraction} />}</CardContent>
            </CardCustom>
          </Grid>
        </Grid>
      )}
    </>
  );
}

export default TrafficTicketDetail;
